import tireRegionPng from "../assets/Images/tire-region.png";
import {TireConfig} from "../models/report.ts";
import {SeasonEnum} from "../constants";
import {DamageSeverity} from "../models/enums/damage-severity.ts";
import {DepthRegion} from "../models/depth-region.ts";

type TireRegionsProps = {
  config: TireConfig;
  depthRegion: DepthRegion;
  season?: SeasonEnum;
  className?: string;
}

const TREAD_BARS = [
  { width: 7.64327, height: 42.6308, transform: "matrix(0.999501 -0.0316035 0.0313585 0.999508 57.4434 9.76807)" },
  { width: 7.64336, height: 43.5528, transform: "matrix(0.998067 -0.0621475 0.061667 0.998097 95.0425 9.01367)" },
  { width: 7.64343, height: 45.1186, transform: "matrix(0.996831 -0.0795532 0.0789396 0.996879 132.211 8.76172)" },
] as const;

const VALUE_BOXES = [
  { d: "M43 29C43 26.7909 44.7909 25 47 25H72C74.2091 25 76 26.7909 76 29V47H43V29Z", textX: 59.5, textY: 38 },
  { d: "M84 29C84 26.7909 85.7909 25 88 25H113C115.209 25 117 26.7909 117 29V47H84V29Z", textX: 100.5, textY: 38 },
  { d: "M125 29C125 26.7909 126.791 25 129 25H154C156.209 25 158 26.7909 158 29V47H125V29Z", textX: 141.5, textY: 38 },
] as const;

const SEVERITY_COLOR: Record<DamageSeverity, string> = {
  [DamageSeverity.HIGH]: "#AE1328",
  [DamageSeverity.LOW]: "#DCB746",
  [DamageSeverity.NONE]: "#226E59",
};

const getSeverity = (config: TireConfig, season: SeasonEnum, depth: number): DamageSeverity => {
  if (depth < config[season].urgentMinimumDepth) return DamageSeverity.HIGH;
  if (depth < config[season].recommendedMinimumDepth) return DamageSeverity.LOW;
  return DamageSeverity.NONE;
}

const TireRegions = ({ config, depthRegion, season, className }: TireRegionsProps) => {
  const resolvedSeason = season || SeasonEnum.UNSPECIFIED;
  const regions = [
    { value: depthRegion.inner, severity: depthRegion.inner != null ? getSeverity(config, resolvedSeason, depthRegion.inner) : DamageSeverity.NONE },
    { value: depthRegion.center, severity: depthRegion.center != null ? getSeverity(config, resolvedSeason, depthRegion.center) : DamageSeverity.NONE },
    { value: depthRegion.outer, severity: depthRegion.outer != null ? getSeverity(config, resolvedSeason, depthRegion.outer) : DamageSeverity.NONE },
  ];

  return (
    <svg
      className={`w-full ${className ?? ""}`}
      viewBox="0 0 200 47"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <g clipPath="url(#clip0)">
        <g clipPath="url(#clip1)">
          <rect x="2.27588" y="-28.019" width="203.092" height="190.141" fill="url(#tirePattern)" />
          {regions.map((region, i) => (
            <g key={i} style={{ mixBlendMode: "difference" }}>
              <rect
                width={TREAD_BARS[i].width}
                height={TREAD_BARS[i].height}
                transform={TREAD_BARS[i].transform}
                fill={SEVERITY_COLOR[region.severity]}
              />
            </g>
          ))}
        </g>
        {regions.map((region, i) => {
          const color = SEVERITY_COLOR[region.severity];
          return (
            <g key={i}>
              <path d={VALUE_BOXES[i].d} fill="white" />
              <path d={VALUE_BOXES[i].d} fill={color} fillOpacity="0.05" />
              <text
                x={VALUE_BOXES[i].textX}
                y={VALUE_BOXES[i].textY}
                textAnchor="middle"
                dominantBaseline="central"
                fill={color}
                fontSize="10"
                fontWeight="bold"
                fontFamily="sans-serif"
              >
                {region.value?.toFixed(1) ?? "-"}
              </text>
            </g>
          );
        })}
      </g>
      <defs>
        <pattern id="tirePattern" patternContentUnits="objectBoundingBox" width="1" height="1">
          <use xlinkHref="#tireImage" transform="matrix(0.0014238 0 0 0.00152672 -0.2119 -0.113544)" />
        </pattern>
        <clipPath id="clip0">
          <path d="M0 3.0413C0 1.36164 1.36163 0 3.0413 0L196.959 0C198.638 0 200 1.36163 200 3.0413V43.9587C200 45.6384 198.638 47 196.959 47L3.04129 47C1.36163 47 0 45.6384 0 43.9587L0 3.0413Z" fill="white" />
        </clipPath>
        <clipPath id="clip1">
          <rect width="202" height="57" fill="white" transform="translate(-1 -5)" />
        </clipPath>
        <image id="tireImage" width="1000" height="655" preserveAspectRatio="none" xlinkHref={tireRegionPng} />
      </defs>
    </svg>
  );
};

export default TireRegions;
